import { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { OrgAvatar } from '@/components/ui/org-avatar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useMyHostOrgsQuery } from '@/hooks/queries/useExternalOrgsQuery'
import { useOrganizationsQuery } from '@/hooks/queries/useOrganizationsQuery'
import { useMyEntities } from '@/hooks/useMyEntities'
import { invalidateSubscriptionCache } from '@/hooks/useOrganizationSubscription'
import { clearOrganizationContextCache } from '@/lib/api/api-client'
import { clientPortalApi } from '@/lib/api/client-portal.api'
import { cn } from '@/lib/utils'
import { useEntityStore } from '@/store/entity'
import { useOrganizationStore } from '@/store/organization.store'
import { useTaskState } from '@/store/taskState'
import { useTodayLayout } from '@/store/today-layout.store'
import { useUserPreferencesStore } from '@/store/user-preferences.store'
import { useUserProfileStore } from '@/store/user-profile.store'
import { BuildingsIcon, CheckIcon, GearIcon } from '@phosphor-icons/react'
import { useQuery } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { ExternalOrgRelationship, Organization } from '@scrambl/shared'

interface DisplayOrganization extends Organization {
  isExternal?: boolean
  relationshipId?: string
  logoUrl?: string | null
  externalRelationship?: ExternalOrgRelationship
}

function getPortalEntityIdFromPath(pathname: string): string | null {
  const parts = pathname.split('/').filter(Boolean)
  return parts[0] === 'client-portal' ? (parts[1] ?? null) : null
}

function clearOrganizationSpecificStores() {
  try {
    useEntityStore.getState().reset?.()
  } catch {
    // Store cleanup is best-effort while switching contexts.
  }

  const taskStore = useTaskState.getState()
  try {
    taskStore.reset?.()
    taskStore.clearSelection?.()
    taskStore.setEditingTask?.(null)
  } catch {
    // Store cleanup is best-effort while switching contexts.
  }

  try {
    useTodayLayout.getState().reset()
  } catch {
    // Store cleanup is best-effort while switching contexts.
  }
}

interface ClientPortalAccessSwitcherProps {
  className?: string
  triggerClassName?: string
}

export function ClientPortalAccessSwitcher({
  className,
  triggerClassName,
}: ClientPortalAccessSwitcherProps = {}) {
  const location = useLocation()
  const navigate = useNavigate()
  const entityId = getPortalEntityIdFromPath(location.pathname)
  const [open, setOpen] = useState(false)
  const currentOrganization = useOrganizationStore(
    state => state.currentOrganization,
  )
  const setCurrentOrganization = useOrganizationStore(
    state => state.setCurrentOrganization,
  )
  const resetOrganizationData = useUserProfileStore(
    state => state.resetOrganizationData,
  )
  const clearUserPreferences = useUserPreferencesStore(
    state => state.clearPreferences,
  )

  const summaryQuery = useQuery({
    queryKey: ['client-portal', entityId, 'summary'],
    queryFn: () => clientPortalApi.getPortalSummary(entityId!),
    enabled: !!entityId,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  })
  const { data: myEntitiesData } = useMyEntities()
  const { data: organizations = [] } = useOrganizationsQuery({
    enabled: true,
  })
  const { data: myHostOrgs = [] } = useMyHostOrgsQuery({
    enabled: true,
  })

  const currentEntity = useMemo(() => {
    const summary = summaryQuery.data
    if (!summary?.entity || !summary.organization) return null
    return {
      id: summary.entity.id,
      name: summary.entity.name,
      organization: {
        id: summary.organization.id,
        name: summary.organization.name,
        logoUrl:
          summary.branding?.logoUrl ??
          summary.organization.logo ??
          summary.entity.logoUrl ??
          null,
      },
    }
  }, [summaryQuery.data])

  const regularOrgs: DisplayOrganization[] = organizations
    .filter(org => Boolean(org.userMembership))
    .map(org => ({ ...org, isExternal: false }))

  const externalOrgs: DisplayOrganization[] = myHostOrgs
    .map(relationship => ({
      id: relationship.hostOrganization?.id || relationship.hostOrganizationId,
      name: relationship.hostOrganization?.name || 'Unknown Organization',
      slug: relationship.hostOrganization?.slug || '',
      logo: relationship.hostOrganization?.logo || null,
      logoUrl: relationship.hostOrganization?.logo || null,
      isExternal: true,
      relationshipId: relationship.id,
      externalRelationship: relationship,
      createdAt: new Date(),
      updatedAt: new Date(),
    }))
    .filter(extOrg => !regularOrgs.some(org => org.id === extOrg.id))

  const portalEntities = myEntitiesData?.entities ?? []
  const displayPortalEntities = currentEntity
    ? [
        ...portalEntities,
        ...(portalEntities.some(entity => entity.entityId === currentEntity.id)
          ? []
          : [
              {
                entityId: currentEntity.id,
                entityName: currentEntity.name,
                organization: {
                  id: currentEntity.organization.id,
                  name: currentEntity.organization.name,
                  logoUrl: currentEntity.organization.logoUrl ?? undefined,
                },
                unreadAttentionCount: 0,
              },
            ]),
      ]
    : portalEntities
  const currentPortalEntity =
    displayPortalEntities.find(entity => entity.entityId === entityId) ?? null

  const handleOrgSelect = (org: DisplayOrganization) => {
    if (!org.id || !org.slug) {
      toast.error('Organization has no slug. Please contact support.')
      return
    }

    resetOrganizationData()
    clearUserPreferences()
    clearOrganizationSpecificStores()
    clearOrganizationContextCache()

    if (currentOrganization?.id) {
      invalidateSubscriptionCache(currentOrganization.id)
    }

    setCurrentOrganization(
      {
        ...org,
        isExternalAccess: org.isExternal === true,
      },
      org.isExternal ? org.externalRelationship : undefined,
    )

    navigate(`/${org.slug}/today`)
    setOpen(false)
  }

  const handlePortalSelect = (portalEntityId: string) => {
    resetOrganizationData()
    clearUserPreferences()
    clearOrganizationSpecificStores()
    clearOrganizationContextCache()
    navigate(`/client-portal/${portalEntityId}`)
    setOpen(false)
  }

  const handleGoToOrganizations = () => {
    navigate('/organizations')
    setOpen(false)
  }

  if (
    !currentPortalEntity &&
    regularOrgs.length === 0 &&
    externalOrgs.length === 0
  ) {
    return null
  }

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'h-8 max-w-[240px] justify-start px-2 transition-colors duration-200 hover:!bg-primary/10',
              triggerClassName,
            )}
          >
            <div className="flex min-w-0 items-center gap-2">
              <OrgAvatar
                organization={{
                  id: currentPortalEntity?.organization.id,
                  name: currentPortalEntity?.organization.name,
                }}
                logoUrl={currentPortalEntity?.organization.logoUrl}
                size="xs"
                className="h-5 w-5 flex-shrink-0"
              />
              <div className="flex min-w-0 flex-col items-start leading-tight">
                <span className="max-w-[170px] truncate text-sm font-medium">
                  {currentPortalEntity?.organization.name ?? 'Select workspace'}
                </span>
                {currentPortalEntity?.entityName ? (
                  <span className="max-w-[170px] truncate text-[11px] text-muted-foreground">
                    {currentPortalEntity.entityName}
                  </span>
                ) : null}
              </div>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="rounded-card w-[300px] bg-background p-0"
          align="end"
          side="bottom"
        >
          <Command className="bg-transparent">
            <CommandInput
              placeholder="Search workspace..."
              className="h-9 bg-background/50 text-sm transition-colors placeholder:text-muted-foreground/60 focus:border-primary/50"
            />
            <CommandList className="max-h-[360px] overflow-y-auto">
              <CommandEmpty className="py-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted/20">
                    <BuildingsIcon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      No workspace found
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Try a different search term
                    </p>
                  </div>
                </div>
              </CommandEmpty>

              {regularOrgs.length > 0 && (
                <CommandGroup heading="Organizations" className="p-1">
                  {regularOrgs.map(org => (
                    <CommandItem
                      key={`org-${org.id}`}
                      value={`${org.name} organization`}
                      onSelect={() => handleOrgSelect(org)}
                      className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <OrgAvatar
                          organization={org}
                          logoUrl={org.logoUrl || undefined}
                          size="xs"
                        />
                        <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                          {org.name}
                        </span>
                      </div>
                      <CheckIcon
                        className={cn(
                          'transition-tonal ml-auto h-3.5 w-3.5 text-primary duration-200',
                          !entityId && currentOrganization?.id === org.id
                            ? 'scale-100 opacity-100'
                            : 'scale-75 opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {displayPortalEntities.length > 0 && (
                <CommandGroup heading="Client Portals" className="p-1">
                  {displayPortalEntities.map(entity => (
                    <CommandItem
                      key={`portal-${entity.entityId}`}
                      value={`${entity.organization.name} ${entity.entityName} client portal`}
                      onSelect={() => handlePortalSelect(entity.entityId)}
                      className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <OrgAvatar
                          organization={entity.organization}
                          logoUrl={entity.organization.logoUrl}
                          size="xs"
                        />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                            {entity.organization.name}
                          </span>
                          <span className="truncate text-xs text-muted-foreground">
                            {entity.entityName}
                          </span>
                        </div>
                      </div>
                      <CheckIcon
                        className={cn(
                          'transition-tonal ml-auto h-3.5 w-3.5 flex-shrink-0 text-primary duration-200',
                          entity.entityId === entityId
                            ? 'scale-100 opacity-100'
                            : 'scale-75 opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {externalOrgs.length > 0 && (
                <CommandGroup heading="External Workspaces" className="p-1">
                  {externalOrgs.map(org => (
                    <CommandItem
                      key={`external-${org.id}`}
                      value={`${org.name} external`}
                      onSelect={() => handleOrgSelect(org)}
                      className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <div className="relative flex-shrink-0">
                          <OrgAvatar
                            organization={org}
                            logoUrl={org.logoUrl || undefined}
                            size="xs"
                          />
                          <div className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full border border-background bg-blue-500">
                            <BuildingsIcon
                              weight="fill"
                              className="h-2 w-2 text-white"
                            />
                          </div>
                        </div>
                        <span className="truncate text-sm font-medium text-foreground transition-colors group-hover:text-primary">
                          {org.name}
                        </span>
                      </div>
                      <CheckIcon
                        className={cn(
                          'transition-tonal ml-auto h-3.5 w-3.5 flex-shrink-0 text-primary duration-200',
                          !entityId && currentOrganization?.id === org.id
                            ? 'scale-100 opacity-100'
                            : 'scale-75 opacity-0',
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <div className="mx-2 h-px bg-border/50" />
              <CommandGroup className="p-1">
                <CommandItem
                  onSelect={handleGoToOrganizations}
                  className="transition-tonal group cursor-pointer rounded-lg px-2 py-2 text-sm text-foreground duration-200 hover:!bg-primary/10 data-[highlighted]:!bg-primary/10"
                >
                  <div className="transition-tonal mr-2 flex h-5 w-5 items-center justify-center rounded-lg bg-muted/20 duration-200 group-hover:bg-primary/10">
                    <GearIcon className="h-3 w-3" />
                  </div>
                  <span className="text-sm font-medium">
                    Manage Organizations
                  </span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
